import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Ticket, CalendarDays } from "lucide-react";
import { api, fileUrl, cachedGet } from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1600119692885-8b04faa7f329?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxkZXZvdGlvbmFsJTIwbXVzaWMlMjBzaW5nZXIlMjBsaXZlJTIwY29uY2VydHxlbnwwfHx8fDE3ODYwMDAwNTZ8MA&ixlib=rb-4.1.0&q=75&w=1400&auto=format";

const Line = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const [heroImg, setHeroImg] = useState(() => localStorage.getItem("sumiran_hero_img") || HERO_IMG);

  useEffect(() => {
    cachedGet("/settings").then(({ data }) => {
      const url = data.hero_image_url ? fileUrl(data.hero_image_url) : HERO_IMG;
      localStorage.setItem("sumiran_hero_img", url);
      setHeroImg(url);
    }).catch(() => {});
  }, []);

  return (
    <section className="relative min-h-[92vh] flex items-center overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0">
        <img src={heroImg} alt="The Band Sumiran performing live" fetchPriority="high" decoding="async" className="w-full h-full object-cover object-[center_18%] md:object-[center_30%]" style={{ filter: "sepia(0.2) saturate(1.25) brightness(1.15)" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/95 via-ink/45 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/40" />
      </div>

      <div className="relative z-10 px-6 md:px-12 max-w-3xl pt-24">
        <h1 className="font-display leading-[1.04] tracking-wide" data-testid="hero-heading">
          <Line delay={0.25} className="text-4xl sm:text-5xl lg:text-6xl text-bone">BHAJANS LIKE</Line>
          <Line delay={0.38} className="text-4xl sm:text-5xl lg:text-6xl text-bone">YOU'VE NEVER</Line>
          <Line delay={0.51} className="text-4xl sm:text-5xl lg:text-6xl text-gold italic">EXPERIENCED BEFORE.</Line>
        </h1>
        <Line delay={0.7} className="mt-6 max-w-md text-base lg:text-lg text-bone/80 font-sans font-light">
          Live Devotional Concerts that blend Bhakti, Music and Community.
        </Line>
        <motion.div
          className="mt-9 flex flex-wrap gap-4"
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <Link to="/book"
            className="flex items-center gap-2.5 px-7 py-3.5 bg-saffron text-ink text-xs font-bold tracking-[0.12em] rounded-full hover:bg-gold hover:scale-[1.03] transition-[background-color,transform]"
            data-testid="hero-book-tickets-btn">
            BOOK TICKETS <Ticket size={15} />
          </Link>
          <Link to="/bookprivateshow"
            className="flex items-center gap-2.5 px-7 py-3.5 border border-bone/40 text-bone text-xs font-bold tracking-[0.12em] rounded-full hover:border-saffron hover:text-saffron hover:scale-[1.03] transition-[border-color,color,transform]"
            data-testid="hero-private-show-btn">
            BOOK PRIVATE SHOW <CalendarDays size={15} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
